import { useAppAuthData } from '@/stores/app/authData';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { getRequisitions } from '@/api/requisitions';
import { libraryGetDocuments } from '@/api/library';
import { useAppRoute } from '@/stores/app/route';
import { getDefaultReqSort } from '@/stores/requisitions/helpers';

const reqModeMap = {
  'draft-requisitions': 'draft',
  'open-requisitions': 'open',
  'closed-requisitions': 'closed',
};

export const getDefaultPostSort = (mode) => ({
  published: '-days_published',
  unpublished: '-unpublished_at',
}[mode] || '-revision_modified_at');

export const getOtherLibraryTypes = async ({
  params,
  selectedMyJobs,
  searchTerm: newSearchTerm,
  selectedFilters,
  updateTotals,
  updateLoading,
  menuPages,
}) => {
  const currentRouteName = useAppRoute().route?.name;
  const { addErrorSnackbar } = useAppSnackbar();

  let reqPagesToFetch = [];

  // Only fetch reqs if requisitions are enabled
  if (useAppAuthData().checkRbacPermissions(['viewRequisitionDocuments'])) {
    reqPagesToFetch = menuPages
      .filter(({ id, label }) => id !== currentRouteName && label.includes('Requisitions')).map(({ id }) => id);
  }

  const permissionsToCheck = {
    published: 'fetchLibraryPublished',
    draft: 'fetchLibraryDrafts',
    unpublished: 'fetchLibraryUnpublished',
    flagship: 'fetchLibraryFlagship',
    templates: 'viewTemplates',
  };

  const postPagesToFetch = menuPages
    .filter(({ id, label }) => id !== currentRouteName
      && !label.includes('Requisitions')
      && useAppAuthData().checkRbacPermissions([permissionsToCheck[id]]))
    .map(({ id }) => id);

  updateLoading(true);

  const isPostPage = ['published', 'draft', 'unpublished', 'flagship'].includes(currentRouteName);

  try {
    const reqPromises = reqPagesToFetch.map((page) => {
      const reqSort = isPostPage ? getDefaultReqSort(reqModeMap[page]) : params.sort;

      return getRequisitions({
        mode: reqModeMap[page] || page,
        params: {
          ...params,
          sort: reqSort,
        },
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      });
    });

    const reqResults = await Promise.allSettled(reqPromises);

    const postPromises = postPagesToFetch.map((page) => {
      const postSort = !isPostPage ? getDefaultPostSort(page) : params.sort;

      return libraryGetDocuments({
        mode: page,
        params: {
          ...params,
          sort: postSort,
        },
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      });
    });

    const postResults = await Promise.allSettled(postPromises);

    const reqTotals = reqResults.map((response, i) => {
      if (response.status === 'fulfilled' && response.value.pagination) {
        return { mode: reqPagesToFetch[i], total: response.value.pagination?.totalDocuments || 0 };
      }
      return null;
    }).filter((totalObj) => Boolean(totalObj));

    const postTotals = postResults.map((response, i) => {
      if (response.status === 'fulfilled' && response.value.pagination) {
        return { mode: postPagesToFetch[i], total: response.value.pagination?.totalDocuments || 0 };
      }
      return null;
    }).filter((totalObj) => Boolean(totalObj));

    updateTotals([...reqTotals, ...postTotals]);
  } catch (error) {
    addErrorSnackbar({ text: `Get other library types - ${error.message}` });
    throw error;
  } finally {
    updateLoading(false);
  }
};
